import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ChangeDetectorRef, Component, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { TableModule } from 'primeng/table';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { DropdownModule } from 'primeng/dropdown';
import { MobileDrawerComponent } from '../mobile-drawer/mobile-drawer.component';
import { broadcast_list, broadcastParticipantOptions } from '../../../models/common/broadcast_list';
import { MatOption, MatSelect, MatSelectModule } from '@angular/material/select';
import { CategoryDisplayLabelPipe } from '../../../shared/category-ordering/category-display-label.pipe';
import { getCategoryDisplayLabel } from '../../../shared/category-ordering/category-ordering.helper';

type DrawerMode = 'participant' | 'category' | 'subCategory' | 'stage';

/** `legacy` = previous production design; `pill` = new reference pill-chip design. */
export type SelectorDesignVariant = 'legacy' | 'pill';

@Component({
  selector: 'stupa-mobile-participant-category-selector',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    TableModule,
    DropdownModule,
    FormsModule,
    ReactiveFormsModule,
    MobileDrawerComponent,
    MatOption,
    MatSelect,
    MatSelectModule,
    CategoryDisplayLabelPipe,
  ],
  templateUrl: './mobile-participant-category-selector.component.html',
  styleUrl: './mobile-participant-category-selector.component.scss',
  host: {
    '[class.selector-design--legacy]': "selectorDesignVariant === 'legacy'",
    '[class.selector-design--pill]': "selectorDesignVariant === 'pill'",
  },
})
export class MobileParticipantCategorySelectorComponent implements OnInit, OnChanges {
  /** Which selector markup/styles to render. Defaults to legacy so production is unchanged. */
  @Input() selectorDesignVariant: SelectorDesignVariant = 'legacy';
  /** When true, renders an inline Previous / Reference toggle above the selectors. */
  @Input() showDesignToggle = false;

  @Output() selectorDesignVariantChange = new EventEmitter<SelectorDesignVariant>();

  @Input() participant_list: any[] = [];
  @Input() category_list: any[] = [];
  @Input() stage_list: any[] = [];
  @Input() disable_participant_selection: boolean = false;

  @Input() active_participant_id: any;
  @Input() active_category_id: any;
  @Input() active_stage: any;

  // Sub-categories related inputs (kept for backward compatibility)
  @Input() selected_parent_category: any = null;
  @Input() sub_categories: any[] = [];
  @Input() show_sub_categories: boolean = false;
  @Input() selected_parent_category_id: any = null;

  @Output() participantChange = new EventEmitter<any>();
  @Output() category_selection = new EventEmitter<any>();
  @Output() stage_selection = new EventEmitter<any>();
  @Output() sub_category_selection = new EventEmitter<any>();
  @Output() sub_category_selection_emit = new EventEmitter<any>();

  screenWidth: any;
  isBroadcastRoute: boolean = false;
  event_type: any = '';
  showSelectorDrawer = false;
  drawerMode: DrawerMode = 'participant';
  pendingParticipantId: any = null;
  pendingCategoryId: any = null;
  pendingSubCategoryId: any = null;
  pendingStage: any = null;

  // Properties for hierarchical category selection
  private _selected_parent_category: any = null;
  private _selected_parent_category_id: any = null;
  private _sub_categories: any[] = [];
  private _show_sub_categories: boolean = false;


  // Broadcast-specific participant options
  private readonly broadcastParticipantOptions = broadcastParticipantOptions;

  // Broadcast-specific video type options (shown when "Streamed" is selected)
  private readonly broadcastVideoTypeOptions = broadcast_list;

  get displayParticipantList(): any[] {
    if (this.isBroadcastRoute) {
      return this.broadcastParticipantOptions || [];
    }
    return Array.isArray(this.participant_list) ? this.participant_list : [];
  }

  get displayCategoryList(): any[] {
    if (this.isBroadcastRoute && this.active_participant_id === 'streamed') {
      return this.broadcastVideoTypeOptions ? this.broadcastVideoTypeOptions.filter((item: any) => item.keep_tab) : [];
    }

    // For non-broadcast routes, filter categories based on selected participant type
    if (!this.isBroadcastRoute) {
      if (!this.category_list) {
        return [];
      }
      // If "All" is selected (id: 0), show all categories
      if (this.active_participant_id === 0 || this.active_participant_id === '0') {
        return this.category_list;
      }
      // Otherwise, filter categories by participant_type_id
      const filtered = this.category_list.filter((category: any) =>
        category.participant_type_id == this.active_participant_id
      );
      return filtered;
    }

    return this.category_list || [];
  }

  get hasParticipantOptions(): boolean {
    const list = this.displayParticipantList;
    return Array.isArray(list) && list.length > 0;
  }

  get hasCategoryOptions(): boolean {
    const list = this.displayCategoryList;
    return Array.isArray(list) && list.length > 0;
  }

  get hasStageOptions(): boolean {
    return Array.isArray(this.stage_list) && this.stage_list.length > 0;
  }

  /** True when a leaf category is selected (required before stages can be shown). */
  get hasSelectedCategory(): boolean {
    const categoryId = this.active_category_id;
    if (
      categoryId === null ||
      categoryId === undefined ||
      categoryId === '' ||
      categoryId === 0 ||
      categoryId === '0'
    ) {
      return false;
    }

    if (!this.isBroadcastRoute && !this.shouldShowCategoryDropdown) {
      return false;
    }

    if (this.internalShowSubCategories && this.hasSubCategoryOptions) {
      return this.internalSubCategories.some(
        (sub: { video_type?: unknown; category_id?: unknown }) =>
          sub != null && (sub.video_type ?? sub.category_id) == categoryId,
      );
    }

    return true;
  }

  /** Stages are category-scoped: hide until a category is selected and stages exist. */
  get shouldShowStageDropdown(): boolean {
    return this.hasSelectedCategory && this.hasStageOptions;
  }

  get hasSubCategoryOptions(): boolean {
    const list = this.internalSubCategories;
    return Array.isArray(list) && list.length > 0;
  }

  get shouldShowCategoryDropdown(): boolean {
    if (this.isBroadcastRoute) {
      return this.hasCategoryOptions;
    }

    if (this.active_participant_id === 0 || this.active_participant_id === '0') {
      return false;
    }

    if (
      typeof this.active_participant_id === 'string' &&
      this.active_participant_id.trim().toLowerCase() === 'all'
    ) {
      return false;
    }

    return this.hasCategoryOptions;
  }

  get categoryLabel(): string {
    if (this.isBroadcastRoute) {
      return 'Video Type';
    }
    // For non-broadcast routes, check event_type
    if (this.event_type === 'T') {
      return 'Category';
    }
    return 'Divisions';
  }

  get drawerTitle(): string {
    switch (this.drawerMode) {
      case 'participant':
        return this.translate.instant('participant_type');
      case 'category':
        return this.categoryLabel;
      case 'subCategory':
        return this.translate.instant('sub_category');
      case 'stage':
        return this.translate.instant('stage');
      default:
        return '';
    }
  }

  get participantTriggerLabel(): string {
    const selected = (this.displayParticipantList || []).find(
      (item: any) => item && item.id == this.active_participant_id,
    );
    if (!selected) {
      return this.translate.instant('participant');
    }
    return this.translate.instant(selected.title || selected.name || 'participant');
  }

  get categoryTriggerLabel(): string {
    const selectedId = this.categoryDropdownValue;
    const selected = (this.displayCategoryList || []).find(
      (item: any) => item && (item.video_type || item.category_id) == selectedId,
    );
    return getCategoryDisplayLabel(selected) || this.categoryLabel;
  }

  get subCategoryTriggerLabel(): string {
    const selected = (this.internalSubCategories || []).find(
      (item: any) => item && (item.video_type || item.category_id) == this.active_category_id,
    );
    return getCategoryDisplayLabel(selected) || this.translate.instant('sub_category');
  }

  get stageTriggerLabel(): string {
    const selected = (this.stage_list || []).find((item: any) => item == this.active_stage);
    return selected ? this.getLocalizedStageName(selected?.name) : this.translate.instant('stage');
  }

  // Getters for sub-categories (maintain backward compatibility)
  get internalSelectedParentCategory(): any {
    return this._selected_parent_category || this.selected_parent_category;
  }

  get internalSelectedParentCategoryId(): any {
    return this._selected_parent_category_id || this.selected_parent_category_id;
  }

  get internalSubCategories(): any[] {
    if (this._sub_categories && this._sub_categories.length > 0) {
      return this._sub_categories;
    }
    return this.sub_categories || [];
  }

  get internalShowSubCategories(): boolean {
    return this._show_sub_categories || this.show_sub_categories;
  }

  // Getter for category dropdown value - shows parent ID when sub-categories are visible, otherwise shows active_category_id
  get categoryDropdownValue(): any {
    if (this.internalShowSubCategories && this.internalSelectedParentCategoryId) {
      return this.internalSelectedParentCategoryId;
    }
    return this.active_category_id;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.screenWidth = document.documentElement.clientWidth;
    this.cdr.detectChanges();

  }

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly router: Router,
    private readonly translate: TranslateService
  ) {
    this.screenWidth = document.documentElement.clientWidth;
    this.event_type = localStorage.getItem('event_type') || '';
    this.checkIfBroadcastRoute();
  }

  setSelectorDesignVariant(variant: SelectorDesignVariant): void {
    if (this.selectorDesignVariant === variant) {
      return;
    }
    this.selectorDesignVariant = variant;
    this.selectorDesignVariantChange.emit(variant);
    this.cdr.markForCheck();
  }

  openDrawer(mode: DrawerMode): void {
    if (mode === 'participant' && this.disable_participant_selection) {
      return;
    }
    this.drawerMode = mode;
    if (mode === 'participant') {
      this.pendingParticipantId = this.active_participant_id;
    } else if (mode === 'category') {
      this.pendingCategoryId = this.categoryDropdownValue;
    } else if (mode === 'subCategory') {
      this.pendingSubCategoryId = this.active_category_id;
    } else if (mode === 'stage') {
      this.pendingStage = this.active_stage;
    }
    this.showSelectorDrawer = true;
  }

  closeDrawerWithoutApply(): void {
    this.showSelectorDrawer = false;
    this.resetPendingSelections();
  }

  applyDrawerSelection(): void {
    this.applyPendingDrawerSelection();
    this.closeDrawerWithoutApply();
  }

  private applyPendingDrawerSelection(): void {
    switch (this.drawerMode) {
      case 'participant':
        if (this.pendingParticipantId != null) {
          this.active_participant_id = this.pendingParticipantId;
          this.handleParticipantChange(this.pendingParticipantId);
        }
        break;
      case 'category':
        if (this.pendingCategoryId != null) {
          this.handleCategorySelection(this.pendingCategoryId);
        }
        break;
      case 'subCategory':
        if (this.pendingSubCategoryId != null) {
          this.handleSubCategorySelection(this.pendingSubCategoryId);
        }
        break;
      case 'stage':
        if (this.pendingStage != null) {
          this.active_stage = this.pendingStage;
          this.handleStageSelection(this.pendingStage);
        }
        break;
    }
  }

  selectPending(value: any): void {
    if (this.drawerMode === 'participant') {
      this.pendingParticipantId = value;
    } else if (this.drawerMode === 'category') {
      this.pendingCategoryId = value;
    } else if (this.drawerMode === 'subCategory') {
      this.pendingSubCategoryId = value;
    } else if (this.drawerMode === 'stage') {
      this.pendingStage = value;
    }
  }

  isPendingSelected(value: any): boolean {
    if (this.drawerMode === 'participant') {
      return this.pendingParticipantId == value;
    }
    if (this.drawerMode === 'category') {
      return this.pendingCategoryId == value;
    }
    if (this.drawerMode === 'subCategory') {
      return this.pendingSubCategoryId == value;
    }
    return this.pendingStage == value;
  }

  private resetPendingSelections(): void {
    this.pendingParticipantId = null;
    this.pendingCategoryId = null;
    this.pendingSubCategoryId = null;
    this.pendingStage = null;
  }

  getLocalizedStageName(stageName: any): string {
    if (typeof stageName !== 'string') {
      return stageName || '';
    }

    const normalized = stageName.trim();
    const groupStageMatch = /^(group\s+stage)(\s+\d+)?$/i.exec(normalized);
    if (groupStageMatch) {
      return `${this.translate.instant('group_stage')}${groupStageMatch[2] || ''}`;
    }

    const knockoutStageMatch = /^(knockout\s+stage)(\s+\d+)?$/i.exec(normalized);
    if (knockoutStageMatch) {
      return `${this.translate.instant('knockout_stage')}${knockoutStageMatch[2] || ''}`;
    }

    return stageName;
  }

  ngOnInit() {
    if (this.isBroadcastRoute && !this.active_participant_id) {
      this.active_participant_id = 'streamed';
      this.handleParticipantChange('streamed');

      // Set default video type to "BROADCAST" (Matches) when streamed is selected
      if (!this.active_category_id) {
        this.active_category_id = 'BROADCAST';
        this.handleCategorySelection('BROADCAST');
      }
    }
    this.syncSubCategoriesFromParent();
    this.initializeSubCategories();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['sub_categories'] ||
      changes['show_sub_categories'] ||
      changes['selected_parent_category'] ||
      changes['selected_parent_category_id']
    ) {
      this.syncSubCategoriesFromParent();
    }

    // Re-initialize sub-categories when category_list or active_category_id changes
    if (changes['category_list'] || changes['active_category_id'] || changes['active_participant_id']) {
      // Use setTimeout to ensure the change detection cycle completes
      setTimeout(() => {
        this.initializeSubCategories();
      });
    }
  }

  /** Mirror parent-provided sub-category state when the page drives selection via @Input. */
  private syncSubCategoriesFromParent(): void {
    if (!this.show_sub_categories || !Array.isArray(this.sub_categories) || this.sub_categories.length === 0) {
      return;
    }

    this._show_sub_categories = true;
    this._sub_categories = this.sub_categories;
    this._selected_parent_category = this.selected_parent_category ?? this._selected_parent_category;
    this._selected_parent_category_id =
      this.selected_parent_category_id ?? this._selected_parent_category_id;
  }

  private initializeSubCategories(): void {
    if (!this.active_category_id || this.isBroadcastRoute) {
      return;
    }

    const categoryList = this.displayCategoryList;
    if (!categoryList?.length) {
      return;
    }

    const expandedCategory = this.findExpandedParentCategory(categoryList);
    if (!expandedCategory) {
      return;
    }

    this.applyParentCategoryExpansion(expandedCategory.parent, expandedCategory.isParentCategory);
  }

  private findExpandedParentCategory(
    categoryList: any[],
  ): { parent: any; isParentCategory: boolean } | null {
    for (const category of categoryList) {
      if (!category?.children?.length) {
        continue;
      }

      const isParentCategory = this.categoryMatchesId(category, this.active_category_id);
      const isChildCategory = category.children.some((child: any) =>
        child && this.categoryMatchesId(child, this.active_category_id),
      );

      if (isParentCategory || isChildCategory) {
        return { parent: category, isParentCategory };
      }
    }
    return null;
  }

  private categoryMatchesId(category: any, activeId: any): boolean {
    return (category.video_type || category.category_id) == activeId;
  }

  private applyParentCategoryExpansion(category: any, isParentCategory: boolean): void {
    this._selected_parent_category = category;
    this._selected_parent_category_id = category.video_type || category.category_id;
    this._sub_categories = category.children;
    this._show_sub_categories = true;
    if (this._selected_parent_category) {
      this._selected_parent_category.isSelected = true;
    }

    if (!isParentCategory) {
      return;
    }

    const firstChild = this._sub_categories[0];
    if (firstChild) {
      this.active_category_id = firstChild.video_type || firstChild.category_id;
      this.category_selection.emit(this.active_category_id);
    }
  }

  private checkIfBroadcastRoute(): void {
    const currentUrl = this.router.url;
    this.isBroadcastRoute = currentUrl.startsWith('/broadcasts');
    if (this.isBroadcastRoute && !this.active_participant_id) {
      this.active_participant_id = 'streamed';
      this.handleParticipantChange('streamed');

      // Set default video type to "BROADCAST" (Matches) when streamed is selected
      if (!this.active_category_id) {
        this.active_category_id = 'BROADCAST';
        this.handleCategorySelection('BROADCAST');
      }
    }
  }

  handleParticipantChange(value: any) {
    
    this.participantChange.emit(value);
    
    // Reset sub-categories when participant changes
    this.resetSubCategorySelections();
  }

  handleCategorySelection(categoryId: any) {
    // Store the selected video_type when in broadcast route and streamed mode
    if (this.isBroadcastRoute && this.active_participant_id === 'streamed') {
      this.active_category_id = categoryId;
      const selectedItem = this.broadcastVideoTypeOptions ? this.broadcastVideoTypeOptions.find(item => item.video_type === categoryId) : null;
      return this.category_selection.emit(selectedItem);
    }
    else {
      const categoryList = this.displayCategoryList;
      if (!categoryList || categoryList.length === 0) {
        return;
      }

      // Find the selected category
      const selectedCategory = categoryList.find((x: any) =>
        x && (x.video_type || x.category_id) == categoryId
      );

      // Reset any existing sub-category selections
      this.resetSubCategorySelections();

      // Check if this category has children
      if (selectedCategory?.children && selectedCategory.children.length > 0) {
        // Case 2: Category has children - show sub-categories
        this._selected_parent_category = selectedCategory;
        this._selected_parent_category_id = selectedCategory.video_type || selectedCategory.category_id;
        this._sub_categories = selectedCategory.children;
        this._show_sub_categories = true;

        // Set the parent as selected
        if (this._selected_parent_category) {
          this._selected_parent_category.isSelected = true;
        }

        const firstChild = this._sub_categories[0];
        if (firstChild) {
          this.active_category_id = firstChild.video_type ?? firstChild.category_id;
        }

        this.category_selection.emit(categoryId);
        return;
      } else {
        // Case 1: Category has no children - work as before
        this._selected_parent_category = null;
        this._sub_categories = [];
        this._show_sub_categories = false;

        this.active_category_id = categoryId;
        this.category_selection.emit(categoryId);
        return;
      }
    }
  }

  resetSubCategorySelections() {
    // Reset all parent categories
    if (this.displayCategoryList && this.displayCategoryList.length > 0) {
      this.displayCategoryList.forEach((cat: any) => {
        if (cat) {
          cat.isSelected = false;
        }
      });
    }

    // Reset sub-categories
    if (this._sub_categories && this._sub_categories.length > 0) {
      this._sub_categories.forEach((sub: any) => {
        if (sub) {
          sub.isSelected = false;
        }
      });
    }

    // Reset parent category ID
    this._selected_parent_category_id = null;
    this._selected_parent_category = null;
    this._show_sub_categories = false;
    this._sub_categories = [];
  }

  sub_category_selection_method(sub_cat_id: any) {
    const subCategories = this.internalSubCategories;
    if (!subCategories.length) {
      return;
    }

    const selectedSubCategory = subCategories.find(
      (sub: any) => sub && (sub.video_type ?? sub.category_id) == sub_cat_id,
    );

    if (selectedSubCategory) {
      this.active_category_id = sub_cat_id;
    }
  }

  handleStageSelection(item: any) {
    this.stage_selection.emit(item);
  }

  handleSubCategorySelection(subCategoryId: any) {
    this.sub_category_selection_method(subCategoryId);
    this.sub_category_selection.emit(subCategoryId);
  }
}
